import { Scene } from './AnimationCanvas';
import { alpha, clear, MONO, Palette, pick, random } from './helpers';

/* Subword pieces for a looping passage describing the transformer architecture. */
export const TRANSFORMER_TOKENS: string[] = [
    'Trans', 'former', 's', ' read', ' every', ' token', ' at', ' once', '.',
    ' Text', ' is', ' split', ' into', ' sub', 'word', ' pieces', ',', ' each',
    ' mapped', ' to', ' a', ' learned', ' embedding', ' vector', '.',
    ' Position', 'al', ' encodings', ' are', ' added', ' so', ' the', ' model',
    ' still', ' knows', ' word', ' order', '.',
    ' Every', ' layer', ' projects', ' the', ' input', ' into', ' queries', ',',
    ' keys', ' and', ' values', '.',
    ' Each', ' query', ' is', ' scored', ' against', ' every', ' key', ',',
    ' scaled', ' by', ' the', ' square', ' root', ' of', ' the', ' head', ' width',
    ',', ' then', ' passed', ' through', ' a', ' soft', 'max', '.',
    ' Those', ' weights', ' blend', ' the', ' values', ',', ' so', ' a', ' token',
    ' pulls', ' context', ' from', ' anywhere', ' in', ' the', ' sequence', '.',
    ' Multiple', ' heads', ' run', ' in', ' parallel', ',', ' each', ' learning',
    ' a', ' different', ' relation', ' \u2014', ' syntax', ',', ' co', 'reference',
    ',', ' topic', '.',
    ' Their', ' outputs', ' are', ' concat', 'enated', ' and', ' projected', ' back',
    ' to', ' the', ' model', ' width', '.',
    ' A', ' residual', ' connection', ' adds', ' the', ' input', ' back', ',', ' and',
    ' layer', ' norm', ' keeps', ' the', ' activations', ' stable', '.',
    ' Next', ' comes', ' a', ' feed', '-', 'forward', ' network', ':', ' expand',
    ' four', 'x', ',', ' apply', ' GELU', ',', ' project', ' down', '.',
    ' That', ' block', ' repeats', ' dozens', ' of', ' times', ',', ' each', ' layer',
    ' refining', ' the', ' representation', '.',
    ' Decoder', 's', ' mask', ' future', ' positions', ',', ' so', ' prediction',
    ' only', ' sees', ' the', ' past', '.',
    ' At', ' inference', ' the', ' keys', ' and', ' values', ' are', ' cached', ',',
    ' making', ' each', ' new', ' token', ' cheap', '.',
    ' Attention', ' costs', ' quad', 'ratic', ' time', ' in', ' sequence', ' length',
    ',', ' which', ' is', ' why', ' long', ' context', ' is', ' hard', '.',
    ' The', ' final', ' hidden', ' state', ' is', ' projected', ' over', ' the',
    ' vocab', 'ulary', ' to', ' give', ' the', ' next', ' token', '.',
];

const softmax = (values: number[]) => {
    const max = Math.max(...values);
    const exps = values.map((v) => Math.exp(v - max));
    const sum = exps.reduce((a, b) => a + b, 0);
    return exps.map((e) => e / sum);
};

const ATTENTION_SPAN = 8;
const TOKENS_PER_SECOND = 6;

export const attentionFlow = (palette: Palette, pieces: string[]): Scene => {
    /* The passage streams endlessly: spent lines are dropped and the scroll rebased. */
    let cursor = 1;
    let live = [pieces[0]];
    let progress = 0;
    let scroll = 0;
    let weights: number[] = [1];

    const scoreAttention = () => {
        const count = Math.min(ATTENTION_SPAN + 1, live.length);
        weights = softmax(Array.from({ length: count }, (_, i) => random(-1.2, 1) + (i / count) * 1.8));
    };

    return (frame) => {
        clear(frame, palette);
        const { ctx, width, height, delta } = frame;

        progress += delta * TOKENS_PER_SECOND;
        while (progress >= 1) {
            progress -= 1;
            live.push(pieces[cursor % pieces.length]);
            cursor += 1;
            scoreAttention();
        }

        const size = Math.max(10, Math.min(14, width / 36));
        const lineHeight = size * 3;
        ctx.font = `${size}px ${MONO}`;
        ctx.textBaseline = 'middle';

        const layout = () => {
            const boxes: { x: number; y: number; w: number; text: string; line: number }[] = [];
            let x = 12;
            let line = 0;
            live.forEach((piece) => {
                const text = piece.replace(' ', '·');
                const w = ctx.measureText(text).width + 8;
                if (x + w > width - 12) {
                    x = 12;
                    line += 1;
                }
                boxes.push({ x, y: 22 + line * lineHeight, w, text, line });
                x += w + 3;
            });
            return { boxes, lines: line + 1 };
        };

        let { boxes, lines } = layout();

        /* Retire the top line once it has scrolled away, rebasing so nothing jumps. */
        if (scroll >= lineHeight * 2) {
            const keepFrom = boxes.findIndex((box) => box.line > 0);
            if (keepFrom > 0) {
                live = live.slice(keepFrom);
                scroll -= lineHeight;
                ({ boxes, lines } = layout());
            }
        }

        const target = Math.max(0, 22 + lines * lineHeight - height + 14);
        scroll += (target - scroll) * Math.min(1, delta * 5);

        const queryIndex = live.length - 1;
        const start = Math.max(0, queryIndex - (weights.length - 1));

        boxes.forEach((box, i) => {
            const y = box.y - scroll;
            if (y < -lineHeight || y > height + lineHeight) return;
            const attended = i >= start && i < queryIndex;
            const weight = attended ? weights[i - start] ?? 0 : 0;

            if (i === queryIndex) {
                ctx.fillStyle = pick(palette, 0);
                ctx.fillRect(box.x, y - size, box.w, size * 2);
                ctx.fillStyle = palette.background;
            } else {
                ctx.fillStyle = alpha(pick(palette, i), 0.06 + weight * 0.85);
                ctx.fillRect(box.x, y - size, box.w, size * 2);
                ctx.fillStyle = alpha(palette.foreground, attended ? 0.95 : 0.5);
            }
            ctx.fillText(box.text, box.x + 4, y);
        });

        const query = boxes[queryIndex];
        if (query) {
            const qx = query.x + query.w / 2;
            const qy = query.y - scroll;
            for (let i = start; i < queryIndex; i++) {
                const weight = weights[i - start] ?? 0;
                if (weight < 0.04) continue;
                const box = boxes[i];
                const bx = box.x + box.w / 2;
                const by = box.y - scroll;
                ctx.strokeStyle = alpha(pick(palette, 0), 0.12 + weight * 0.8);
                ctx.lineWidth = 0.5 + weight * 6;
                ctx.beginPath();
                ctx.moveTo(qx, qy + size);
                if (Math.abs(by - qy) < 1) {
                    ctx.quadraticCurveTo((qx + bx) / 2, qy + size + lineHeight * 0.42, bx, by + size);
                } else {
                    ctx.bezierCurveTo(qx, qy + size + lineHeight * 0.4, bx, by + size + lineHeight * 0.4, bx, by + size);
                }
                ctx.stroke();
            }

            const best = weights.indexOf(Math.max(...weights));
            const bestBox = boxes[start + best];
            if (bestBox) {
                ctx.font = `${size * 0.62}px ${MONO}`;
                ctx.fillStyle = alpha(palette.foreground, 0.7);
                ctx.fillText(
                    weights[best].toFixed(2),
                    bestBox.x + bestBox.w / 2 - 8,
                    bestBox.y - scroll + size * 2.1
                );
            }
        }

        ctx.fillStyle = palette.background;
        ctx.fillRect(0, 0, width, 10);
        ctx.fillRect(0, height - 10, width, 10);
    };
};
